import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface UseAutoSaveProps {
	taskId: string
	formData: Record<string, string | number | boolean | null | undefined>
	isEnabled?: boolean
	debounceMs?: number
	hiddenComponentIds?: Set<string>
}

export const transformFormDataToResponses = (
	data: Record<string, string | number | boolean | null | undefined>,
	hiddenComponentIds?: Set<string>,
) => {
	return Object.entries(data)
		.filter(([id, response]) => !hiddenComponentIds?.has(id) && response !== null && response !== undefined)
		.map(([componentId, response]) => ({
			componentId,
			response: String(response),
		}))
}

export const useAutoSave = ({
	taskId,
	formData,
	isEnabled = true,
	debounceMs = 800,
	hiddenComponentIds,
}: UseAutoSaveProps) => {
	const [status, setStatus] = useState<AutoSaveStatus>('idle')
	const [lastSavedData, setLastSavedData] = useState<Record<string, string | number | boolean | null | undefined>>({})
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const isInitialRender = useRef(true)
	const hasInitialDataLoaded = useRef(false)
	const toast = useToast()

	const autoSaveMutation = useMutation({
		...trpc.client.tasks.saveForm.mutationOptions({
			onMutate: () => {
				setStatus('saving')
			},
			onSuccess: () => {
				setStatus('saved')
				setLastSavedData(formData)
				toast.showToast('Changes saved')

				const actualQueryKey = trpc.client.tasks.show.queryOptions({
					taskId,
				}).queryKey
				queryClient.invalidateQueries({
					queryKey: actualQueryKey,
				})
				queryClient.invalidateQueries({
					queryKey: ['cycleExecution', 'show'],
				})
				setTimeout(() => setStatus('idle'), 2000)
			},
			onError: () => {
				setStatus('error')
				toast.showToast('Failed to auto-save changes', 'error')
				setTimeout(() => setStatus('idle'), 3000)
			},
		}),
	})

	const hasFormChanged = () => {
		if (!formData || Object.keys(formData).length === 0) return false
		const isHidden = (id: string) => hiddenComponentIds?.has(id) ?? false
		const formKeys = Object.keys(formData).filter((id) => !isHidden(id))
		const savedKeys = Object.keys(lastSavedData).filter((id) => !isHidden(id))
		if (formKeys.length !== savedKeys.length) return true
		for (const key of formKeys) {
			if (formData[key] !== lastSavedData[key]) return true
		}
		return false
	}

	const transformResponses = (data: Record<string, string | number | boolean | null | undefined>) => {
		return transformFormDataToResponses(data, hiddenComponentIds)
	}

	const triggerAutoSave = () => {
		if (!isEnabled || !hasFormChanged() || autoSaveMutation.isPending) {
			return
		}

		const responses = transformResponses(formData)

		autoSaveMutation.mutate({ taskId, responses })
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: hasFormChanged and triggerAutoSave are intentionally non-stable closures over formData
	useEffect(() => {
		if (isInitialRender.current) {
			isInitialRender.current = false
			if (formData && Object.keys(formData).length > 0) {
				setLastSavedData(formData)
				hasInitialDataLoaded.current = true
			}
			return
		}

		if (!hasInitialDataLoaded.current && formData && Object.keys(formData).length > 0) {
			setLastSavedData(formData)
			hasInitialDataLoaded.current = true
			return
		}

		if (!hasInitialDataLoaded.current || !hasFormChanged()) {
			return
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}

		timeoutRef.current = setTimeout(() => {
			triggerAutoSave()
		}, debounceMs)

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [formData, debounceMs])

	return {
		status,
		isAutoSaving: autoSaveMutation.isPending,
		hasUnsavedChanges: hasFormChanged(),
	}
}
